import { useEffect, useRef } from "react";
import { renderMineProdChart, validateChart } from "./renderMineProdChart";

/**
 * React wrapper around the canonical, dependency-free MineProd SVG renderer
 * (src/components/Layout/renderMineProdChart.js). Draws every mineprod.chart/1.x
 * payload (line, bar, horizontal_bar, stacked_bar, combo_bar_line, waterfall,
 * donut, scatter, heatmap) with the renderer's own theming, tooltips, legend
 * toggling, type switch and table fallback.
 */

/** True when the payload is a structurally valid, drawable MineProd chart. */
export function isMineProdChart(obj) {
  return !!obj && typeof obj === "object" && validateChart(obj).length === 0;
}

/**
 * Parse the body of a ```mineprod-chart / ```json fenced code block into a chart
 * payload (bare chart or `{ chart }` envelope), or null when it is not one.
 */
export function parseChartPayload(raw) {
  if (typeof raw !== "string" || raw.indexOf("mineprod.chart/") === -1) return null;
  try {
    const parsed = JSON.parse(raw.trim());
    if (isMineProdChart(parsed)) return parsed;
    if (parsed && isMineProdChart(parsed.chart)) return parsed.chart;
  } catch {
    /* not valid JSON */
  }
  return null;
}

export default function MineProdChart({
  chart,
  isDark,
  theme,
  allowTypeSwitch = true,
  allowCopy = true,
  showTable = false,
  frame = null,
  // Skip integrity.checksum verification by default: the chatbot trusts the
  // Brain response and should still draw if a checksum scheme differs.
  verify = false,
  onError,
  onTypeChange,
  onCopy,
  className,
  style,
}) {
  const ref = useRef(null);
  const handle = useRef(null);
  const shown = useRef(null);
  const callbacks = useRef({ onError, onTypeChange, onCopy });
  callbacks.current = { onError, onTypeChange, onCopy };

  const resolvedTheme = theme || (isDark === true ? "dark" : isDark === false ? "light" : "auto");

  // (re)create the renderer when a frame-affecting option changes
  useEffect(() => {
    if (!ref.current || !chart) return undefined;
    shown.current = chart;
    handle.current = renderMineProdChart(ref.current, chart, {
      theme: resolvedTheme,
      allowTypeSwitch,
      allowCopy,
      showTable,
      frame,
      verify,
      onError: (p) => (callbacks.current.onError ? callbacks.current.onError(p) : console.warn("[mineprod-chart] not drawn:", p)),
      onTypeChange: (t) => callbacks.current.onTypeChange && callbacks.current.onTypeChange(t),
      onCopy: (r) => callbacks.current.onCopy && callbacks.current.onCopy(r),
    });
    return () => {
      handle.current && handle.current.destroy();
      handle.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowTypeSwitch, allowCopy, showTable, frame, verify, chart == null]);

  // new payload -> update in place (keeps the instance)
  useEffect(() => {
    if (handle.current && chart && chart !== shown.current) {
      shown.current = chart;
      handle.current.update(chart);
    }
  }, [chart]);

  useEffect(() => {
    if (handle.current) handle.current.setTheme(resolvedTheme);
  }, [resolvedTheme]);

  if (!chart) return null;
  return <div ref={ref} className={className} style={style} data-mineprod-chart={chart.chart_id} />;
}
